import React, { Fragment } from 'react'
import { node, object, bool } from 'prop-types'
import { createComponent } from '@ycos/fela'
import { TwoColumnItemList, ThreeColumnItemList } from '@ycos/component-columns'
import { hasTag } from '@ycos/coremedia-renderer'
import { fullBleedStyles } from '@ycos/layout-fullbleed'
import LayoutWithMargins from '@ycos/layout-with-margins'
import HeadlineWithCopy from './HeadlineWithCopy'

const SectionContainer = createComponent(
  'SectionContainer',
  ({
    greySection,
    noMarginTop,
    noBottomMargin,
    hasTopBar,
    theme: {
      typography,
      spacingMultiplier,
      custom: { section = {} }
    },
    additionalStyles
  }) => {
    const {
      grayMarginTopSmallMultiplier,
      grayMarginTopMediumMultiplier,
      grayMarginTopLargeMultiplier,
      grayMarginTopXLargeMultiplier,
      grayPaddingTopSmallMultiplier,
      grayPaddingTopMediumMultiplier,
      grayPaddingTopLargeMultiplier,
      grayPaddingTopXLargeMultiplier,
      grayPaddingBottomSmallMultiplier = 7,
      grayPaddingBottomMediumMultiplier = 10,
      grayPaddingBottomLargeMultiplier = 10,
      grayPaddingBottomXLargeMultiplier = 10,
      marginTopSmallMultiplier,
      marginTopMediumMultiplier,
      marginTopLargeMultiplier,
      marginTopXLargeMultiplier,
      marginBottomSmallMultiplier,
      marginBottomMediumMultiplier,
      marginBottomLargeMultiplier,
      marginBottomXLargeMultiplier
    } = section
    const greySectionStyles = {
      ...fullBleedStyles,
      marginTop: noMarginTop ? 0 : `${grayMarginTopSmallMultiplier * spacingMultiplier}px`,
      paddingTop: noMarginTop ? 0 : `${grayPaddingTopSmallMultiplier * spacingMultiplier}px`,
      paddingBottom: `${grayPaddingBottomSmallMultiplier * spacingMultiplier}px`,
      backgroundColor: typography.colors.PrimaryGrey,

      'screen-medium': {
        marginTop: noMarginTop ? 0 : `${grayMarginTopMediumMultiplier * spacingMultiplier}px`,
        paddingTop: noMarginTop ? 0 : `${grayPaddingTopMediumMultiplier * spacingMultiplier}px`,
        paddingBottom: `${grayPaddingBottomMediumMultiplier * spacingMultiplier}px`
      },

      'screen-large': {
        marginTop: noMarginTop ? 0 : `${grayMarginTopLargeMultiplier * spacingMultiplier}px`,
        paddingTop: noMarginTop ? 0 : `${grayPaddingTopLargeMultiplier * spacingMultiplier}px`,
        paddingBottom: `${grayPaddingBottomLargeMultiplier * spacingMultiplier}px`
      },

      'screen-xlarge': {
        marginTop: noMarginTop ? 0 : `${grayMarginTopXLargeMultiplier * spacingMultiplier}px`,
        paddingTop: noMarginTop ? 0 : `${grayPaddingTopXLargeMultiplier * spacingMultiplier}px`,
        paddingBottom: `${grayPaddingBottomXLargeMultiplier * spacingMultiplier}px`
      }
    }

    return {
      paddingTop: noMarginTop ? 0 : `${marginTopSmallMultiplier * spacingMultiplier}px`,
      marginBottom: noBottomMargin ? 0 : `${marginBottomSmallMultiplier * spacingMultiplier}px`,
      width: '100%',
      borderTop: hasTopBar ? `4px solid ${typography.colors.PrimaryBlack}` : undefined,
      ...(greySection && greySectionStyles),
      ...additionalStyles,

      'screen-medium': {
        paddingTop: noMarginTop ? 0 : `${marginTopMediumMultiplier * spacingMultiplier}px`,
        marginBottom: noBottomMargin ? 0 : `${marginBottomMediumMultiplier * spacingMultiplier}px`,
        ...(greySection && greySectionStyles['screen-medium']),
        ...additionalStyles['screen-medium']
      },

      'screen-large': {
        paddingTop: noMarginTop ? 0 : `${marginTopLargeMultiplier * spacingMultiplier}px`,
        marginBottom: noBottomMargin ? 0 : `${marginBottomLargeMultiplier * spacingMultiplier}px`,
        ...(greySection && greySectionStyles['screen-large']),
        ...additionalStyles['screen-large']
      },

      'screen-xlarge': {
        paddingTop: noMarginTop ? 0 : `${marginTopXLargeMultiplier * spacingMultiplier}px`,
        marginBottom: noBottomMargin ? 0 : `${marginBottomXLargeMultiplier * spacingMultiplier}px`,
        ...(greySection && greySectionStyles['screen-xlarge']),
        ...additionalStyles['screen-xlarge']
      }
    }
  },
  'section'
)

const IdentityComponent = ({ children }) => <Fragment>{children}</Fragment>

const Section = ({ item, children, titleComponent, additionalStyles, noMarginTop, noBottomMargin, topBar }) => {
  // we are moving towards uniformizing prop and tag names, e.g. noBottomMargin & Section.NoBottomMargin

  const { teaserTextPlain, teaserTitle, layoutVariant } = item

  const ColumnComponent =
    {
      'isc-section': IdentityComponent,
      'isc-section-2-col': TwoColumnItemList,
      'isc-section-3-col': ThreeColumnItemList
    }[layoutVariant] || IdentityComponent

  const verticallyCenter = hasTag(item.tags, 'Columns.VerticallyCenter')
  const Content = (
    <ColumnComponent item={{ layoutVariant }} verticallyCenter={verticallyCenter}>
      {children}
    </ColumnComponent>
  )

  const hasHeadlineCopySection = teaserTextPlain || teaserTitle
  const isGreySection = hasTag(item.tags, 'Theme.Gray')
  const hasNoMarginTop = noMarginTop || hasTag(item.tags, 'Section.NoTopMargin')
  const hasNoBottomMargin = noBottomMargin || hasTag(item.tags, 'Section.NoBottomMargin')
  const hasTopBar = topBar || hasTag(item.tags, 'Section.TopBar')
  const Title = titleComponent

  return (
    <SectionContainer
      hasHeadlineCopySection={hasHeadlineCopySection}
      greySection={isGreySection}
      noMarginTop={hasNoMarginTop}
      noBottomMargin={hasNoBottomMargin}
      hasTopBar={hasTopBar}
      additionalStyles={additionalStyles}
      data-automation={layoutVariant}
    >
      {hasHeadlineCopySection && <Title item={item} isGreySection={isGreySection} />}
      {isGreySection ? <LayoutWithMargins>{Content}</LayoutWithMargins> : Content}
    </SectionContainer>
  )
}

Section.layoutVariant = ['isc-section', 'isc-section-2-col', 'isc-section-3-col']

Section.propTypes = {
  titleComponent: node,
  additionalStyles: object,
  noMarginTop: bool,
  topBar: bool
}

Section.defaultProps = {
  titleComponent: HeadlineWithCopy,
  additionalStyles: {},
  noMarginTop: false,
  noBottomMargin: false,
  topBar: false
}

export default Section
